import type { Column } from "../components/ui/DataTable";

/** Bouw een schoon A4-HTML-document met alleen de tabel (titel + zichtbare
 *  rijen/kolommen) en druk af via een off-screen iframe. Werkt onafhankelijk van
 *  de globale print-CSS, dus gegarandeerd een schone tabel-PDF zonder de rest
 *  van de pagina. */
export function printRowsAsPdf<T>(title: string, rows: T[], columns: Column<T>[]) {
  const visibleCols = columns.filter((c) => c.key !== "actions");

  const head = visibleCols.map((c) => `<th>${escapeHtml(c.header)}</th>`).join("");
  const body = rows
    .map(
      (row) =>
        `<tr>${visibleCols
          .map((c) => `<td>${escapeHtml(String(c.accessor(row) ?? ""))}</td>`)
          .join("")}</tr>`
    )
    .join("");

  const html = `<!DOCTYPE html>
<html lang="nl">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1e293b; }
  h1 { font-size: 18px; margin: 0 0 12px; }
  .meta { font-size: 11px; color: #64748b; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { background: #f1f5f9; text-align: left; padding: 6px 8px; border: 1px solid #cbd5e1; font-weight: 700; }
  td { padding: 5px 8px; border: 1px solid #e2e8f0; vertical-align: top; }
  tr:nth-child(even) td { background: #f8fafc; }
  @media print { .no-print { display: none; } }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="meta">${rows.length} ${rows.length === 1 ? "regel" : "regels"} &middot; AIBoek</div>
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body || `<tr><td colspan="${visibleCols.length}" style="text-align:center;color:#94a3b8">Geen rijen</td></tr>`}</tbody>
  </table>
</body>
</html>`;

  const existing = document.getElementById("aiboek-print-iframe") as HTMLIFrameElement | null;
  if (existing) existing.remove();
  const iframe = document.createElement("iframe");
  iframe.id = "aiboek-print-iframe";
  iframe.style.position = "fixed";
  iframe.style.left = "-10000px";
  iframe.style.top = "0";
  iframe.style.width = "1100px";
  iframe.style.height = "800px";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  const doc = iframe.contentDocument;
  if (!win || !doc) return;

  let printed = false;
  const trigger = () => {
    if (printed) return;
    printed = true;
    try {
      win.focus();
      win.print();
    } catch {
      /* ignore */
    }
    setTimeout(() => iframe.remove(), 4000);
  };

  iframe.addEventListener("load", () => setTimeout(trigger, 200));
  setTimeout(trigger, 800);

  doc.open();
  doc.write(html);
  doc.close();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}